import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Star, Inbox, User, Crown, Scissors, LayoutDashboard, Mail, Store, Users, Megaphone } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SellPayouts from "@/components/SellPayouts";
import EmailMarketing from "@/components/EmailMarketing";
import { startCheckout } from "@/lib/checkout";
import { hasLeadAccess } from "@/lib/access";

const PLAN_LABELS = {
  client_premium: "Client Premium", insider: "Barber Insider", certified: "Quality Certified",
  featured: "Featured Pro", shop_standard: "Shop", shop_featured: "Featured Shop",
  vendor_entry: "Vendor", vendor_premium: "Vendor Premium", educator: "Educator",
};

const PROFILE_ROLES = ["barber", "shop", "educator", "admin"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [barber, setBarber] = useState(null);
  const [tab, setTab] = useState("overview");
  const [form, setForm] = useState({
    display_name: "", shop_name: "", city: "", state: "", bio: "", instagram: "", booking_url: "",
    specialties: "", portfolio: "", avatar: "", phone: "",
  });
  const [saving, setSaving] = useState(false);

  const hasProfile = PROFILE_ROLES.includes(user?.role);
  const canSell = ["educator", "vendor", "admin"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [waitlist, setWaitlist] = useState(null);

  useEffect(() => {
    api.get("/me/stats").then(({ data }) => setStats(data)).catch(() => {});
    if (hasProfile) {
      api.get("/me/barber").then(({ data }) => {
        if (data) {
          setBarber(data);
          setForm({
            display_name: data.display_name || "", shop_name: data.shop_name || "",
            city: data.city || "", state: data.state || "", bio: data.bio || "",
            instagram: data.instagram || "", booking_url: data.booking_url || "",
            specialties: (data.specialties || []).join(", "), portfolio: (data.portfolio || []).join(", "),
            avatar: data.avatar || "", phone: data.phone || "",
          });
        }
      }).catch(() => {});
    }
    if (["barber", "admin"].includes(user?.role)) {
      api.get("/me/leads").then(({ data }) => setLeads(data)).catch(() => {});
    }
  }, [user, hasProfile]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name, shop_name: form.shop_name, city: form.city, state: form.state,
        bio: form.bio, instagram: form.instagram, booking_url: form.booking_url, avatar: form.avatar,
        phone: form.phone,
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
        portfolio: form.portfolio.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/barbers", payload);
      setBarber(data);
      toast.success("Profile saved.");
    } catch {
      toast.error("Could not save profile.");
    } finally { setSaving(false); }
  };

  const planLabel = user?.plan ? PLAN_LABELS[user.plan] : null;
  const active = user?.plan_status === "active";

  const NAV = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    ...(hasProfile ? [{ id: "profile", label: "My Profile", icon: User }] : []),
    ...(["barber", "admin"].includes(user?.role) ? [{ id: "leads", label: "Leads", icon: Inbox }] : []),
    ...(canSell ? [{ id: "sell", label: "Sell & Payouts", icon: Store }] : []),
    ...(isAdmin ? [{ id: "waitlist", label: "Waitlist", icon: Users }] : []),
    ...(isAdmin ? [{ id: "marketing", label: "Marketing", icon: Megaphone }] : []),
    { id: "membership", label: "Membership", icon: Crown },
  ];

  const loadWaitlist = () => api.get("/waitlist").then(({ data }) => setWaitlist(data)).catch(() => setWaitlist({ count: 0, entries: [] }));

  useEffect(() => {
    if (tab === "waitlist" && isAdmin && !waitlist) loadWaitlist();
  }, [tab, isAdmin, waitlist]);

  // Resume a plan the user picked before signing up (checkout continuity).
  useEffect(() => {
    const pending = sessionStorage.getItem("pending_plan");
    if (user && pending) {
      sessionStorage.removeItem("pending_plan");
      startCheckout(pending, user.id).catch(() => toast.error("Could not resume checkout. Try again from Pricing."));
    }
  }, [user]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* sidebar */}
        <aside className="space-y-4">
          <div className="bg-[#111] border border-white/10 p-5">
            <div className="flex items-center gap-3">
              {user?.picture ? <img src={user.picture} alt="" className="w-11 h-11 object-cover rounded-full" /> : <div className="w-11 h-11 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-display font-bold">{user?.name?.[0] || "B"}</div>}
              <div className="min-w-0">
                <p className="font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-neutral-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              {active ? (
                <span className="flex items-center gap-1 text-xs bg-[#D4AF37] text-black font-mono px-2 py-1"><Crown size={11} /> {planLabel}</span>
              ) : (
                <span className="text-xs text-neutral-500 font-mono">Free account</span>
              )}
            </div>
          </div>

          <nav className="bg-[#111] border border-white/10 p-2">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => setTab(n.id)} data-testid={`dash-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${tab === n.id ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-neutral-300 hover:bg-white/5"}`}>
                <n.icon size={16} /> {n.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* content */}
        <div>
          {tab === "overview" && (
            <div data-testid="dash-overview">
              <h1 className="font-display text-3xl font-bold">Welcome back, {user?.name?.split(" ")[0]}.</h1>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Plan" value={active ? planLabel : "Free"} icon={Crown} />
                <Stat label="Certified" value={stats?.certified ? "Yes" : "No"} icon={ShieldCheck} gold={stats?.certified} />
                <Stat label="Featured" value={stats?.featured ? "Yes" : "No"} icon={Star} gold={stats?.featured} />
                <Stat label="Leads" value={stats?.leads ?? 0} icon={Inbox} />
              </div>

              {!active && (
                <div className="mt-8 border border-[#D4AF37]/30 bg-[#111] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-semibold">Unlock trust, leads & exposure</h3>
                    <p className="text-sm text-neutral-400 mt-1">Get the Quality Certified badge and start receiving client messages.</p>
                  </div>
                  <Link to="/pricing"><Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold" data-testid="dash-upgrade">Upgrade</Button></Link>
                </div>
              )}

              {hasProfile && !stats?.profile_complete && (
                <div className="mt-6 border border-white/10 bg-[#0a0a0a] p-6">
                  <p className="text-sm text-neutral-300"><Scissors size={15} className="inline text-[#D4AF37] mr-2" />Complete your profile to qualify for certification and get discovered.</p>
                  <Button onClick={() => setTab("profile")} variant="outline" className="mt-4 rounded-none border-white/20 bg-transparent text-white hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="dash-complete-profile">Complete profile</Button>
                </div>
              )}
            </div>
          )}

          {tab === "profile" && hasProfile && (
            <div data-testid="dash-profile">
              <h1 className="font-display text-3xl font-bold">My Profile</h1>
              <p className="mt-2 text-sm text-neutral-400">This is what clients see in the directory. {barber && <Link to={`/barber/${barber.id}`} className="text-[#D4AF37]">View public profile →</Link>}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Field label="Display name" v={form.display_name} set={(x) => setForm({ ...form, display_name: x })} testid="pf-name" />
                <Field label="Shop name" v={form.shop_name} set={(x) => setForm({ ...form, shop_name: x })} testid="pf-shop" />
                <Field label="City" v={form.city} set={(x) => setForm({ ...form, city: x })} testid="pf-city" />
                <Field label="State" v={form.state} set={(x) => setForm({ ...form, state: x })} testid="pf-state" />
                <Field label="Instagram handle" v={form.instagram} set={(x) => setForm({ ...form, instagram: x })} testid="pf-ig" />
                <Field label="Booking URL" v={form.booking_url} set={(x) => setForm({ ...form, booking_url: x })} testid="pf-booking" />
                <Field label="Phone for SMS alerts (e.g. +14155552671)" v={form.phone} set={(x) => setForm({ ...form, phone: x })} testid="pf-phone" />
                <Field label="Avatar image URL" v={form.avatar} set={(x) => setForm({ ...form, avatar: x })} testid="pf-avatar" />
                <Field label="Specialties (comma separated)" v={form.specialties} set={(x) => setForm({ ...form, specialties: x })} testid="pf-spec" />
              </div>
              <div className="mt-4">
                <Label className="text-neutral-300">Bio</Label>
                <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="pf-bio" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
              </div>
              <div className="mt-4">
                <Label className="text-neutral-300">Portfolio image URLs (comma separated)</Label>
                <Textarea value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} data-testid="pf-portfolio" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
              </div>
              <Button onClick={saveProfile} disabled={saving} data-testid="pf-save" className="mt-6 rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold px-8">{saving ? "Saving…" : "Save profile"}</Button>
              {!active && <p className="mt-3 text-xs text-neutral-500">Certification badge appears once you're on the Quality Certified plan or higher.</p>}
            </div>
          )}

          {tab === "leads" && (
            <div data-testid="dash-leads">
              <h1 className="font-display text-3xl font-bold">Client Messages</h1>
              {!hasLeadAccess(user) ? (
                <div className="mt-6 border border-[#D4AF37]/40 bg-[#111] p-8 text-center gold-glow" data-testid="leads-locked">
                  <Inbox className="mx-auto text-[#D4AF37]" size={28} />
                  {stats?.leads > 0 ? (
                    <>
                      <p className="mt-4 font-display text-2xl font-bold text-white">You have <span className="gold-text">{stats.leads}</span> client message{stats.leads === 1 ? "" : "s"} waiting.</p>
                      <p className="mt-2 text-neutral-400">Clients in your area are trying to book you. Upgrade to <span className="text-[#D4AF37] font-semibold">Booked Barber</span> ($49.99/mo) to read and respond.</p>
                    </>
                  ) : (
                    <p className="mt-4 text-neutral-300">Client messages unlock with the <span className="text-[#D4AF37] font-semibold">Booked Barber</span> (Quality Certified) plan. When a client in your area reaches out, you'll be ready.</p>
                  )}
                  <Link to="/pricing" className="inline-block mt-5"><Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold" data-testid="leads-upgrade">Become a Booked Barber</Button></Link>
                </div>
              ) : leads.length === 0 ? (
                <p className="mt-6 text-neutral-500">No messages yet. Complete your profile so clients in your area can find and message you.</p>
              ) : (
                <div className="mt-6 space-y-3">
                  {leads.map((l) => (
                    <div key={l.id} className="bg-[#111] border border-white/10 p-5" data-testid={`lead-${l.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{l.name}</p>
                        <span className="text-xs text-neutral-500">{new Date(l.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-[#D4AF37] flex items-center gap-1.5 mt-1"><Mail size={13} /> {l.contact}</p>
                      <p className="mt-2 text-sm text-neutral-300">{l.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "sell" && canSell && <SellPayouts role={user?.role} />}

          {tab === "marketing" && isAdmin && <EmailMarketing />}

          {tab === "waitlist" && isAdmin && (
            <div data-testid="dash-waitlist">
              <div className="flex items-center justify-between">
                <h1 className="font-display text-3xl font-bold">Founding Waitlist</h1>
              </div>
              <p className="mt-2 text-sm text-neutral-400">Leads captured from the homepage — {waitlist?.count ?? 0} total.</p>
              {!waitlist ? (
                <p className="mt-6 text-neutral-500">Loading…</p>
              ) : waitlist.entries.length === 0 ? (
                <p className="mt-6 text-neutral-500">No signups yet. Share your homepage to start collecting founding members.</p>
              ) : (
                <div className="mt-6 overflow-x-auto border border-white/10">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead><tr className="bg-[#0a0a0a] border-b border-white/10 text-left text-xs font-mono uppercase text-neutral-400">
                      <th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Role</th><th className="p-3">City</th><th className="p-3">Joined</th>
                    </tr></thead>
                    <tbody>
                      {waitlist.entries.map((e) => (
                        <tr key={e.id} className="border-b border-white/5" data-testid={`waitlist-row-${e.id}`}>
                          <td className="p-3 text-[#D4AF37]">{e.email}</td>
                          <td className="p-3">{e.name || "—"}</td>
                          <td className="p-3 capitalize">{e.role}</td>
                          <td className="p-3">{e.city || "—"}</td>
                          <td className="p-3 text-neutral-500">{new Date(e.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "membership" && (
            <div data-testid="dash-membership">
              <h1 className="font-display text-3xl font-bold">Membership</h1>
              <div className="mt-6 bg-[#111] border border-white/10 p-6">
                <p className="text-sm text-neutral-400">Current plan</p>
                <p className="mt-1 font-display text-2xl font-bold gold-text">{active ? planLabel : "Free"}</p>
                <p className="mt-1 text-xs text-neutral-500">Status: {user?.plan_status}</p>
                <div className="mt-5 flex gap-3">
                  <Link to="/pricing"><Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold" data-testid="membership-change">{active ? "Change plan" : "Choose a plan"}</Button></Link>
                  <Button onClick={logout} variant="outline" className="rounded-none border-white/20 bg-transparent text-white hover:border-red-500 hover:text-red-400" data-testid="membership-logout">Log out</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, gold }) {
  return (
    <div className="bg-[#111] border border-white/10 p-5">
      <Icon size={18} className={gold ? "text-[#D4AF37]" : "text-neutral-500"} />
      <p className="mt-3 text-2xl font-display font-bold">{value}</p>
      <p className="text-xs text-neutral-500 mt-0.5">{label}</p>
    </div>
  );
}

function Field({ label, v, set, testid }) {
  return (
    <div>
      <Label className="text-neutral-300">{label}</Label>
      <Input value={v} onChange={(e) => set(e.target.value)} data-testid={testid} className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
    </div>
  );
}
